import React, { useState, useMemo } from 'react';
import { 
  BookOpen, 
  Trash2, 
  Play, 
  ChevronDown, 
  ChevronUp,
  Clock,
  CheckCircle,
  Circle,
  X,
  BarChart3,
  FolderOpen
} from 'lucide-react';
import { StoredProcessedBook, BookReadingProgress, ChapterProgress } from '../utils/indexedDB';
import { CategoryItem } from '../types/category';
import { getChapterStatus } from '../hooks/useMultiBookProgress';
import { formatWordCount, estimateTime } from '../utils/format';

interface BookCardProps {
  book?: StoredProcessedBook;
  categoryItem?: CategoryItem;
  progress?: BookReadingProgress | null;
  onContinueReading?: (bookId: string, chapterId: string, wordIndex: number) => void;
  onStartFromChapter?: (bookId: string, chapterId: string) => void;
  onDelete?: (bookId: string) => void;
  onRead?: (item: CategoryItem) => void;
  coverUrl?: string | null;
}

/**
 * BookCard Component
 * Displays a processed book OR a category item with progress, chapter selection, and actions
 */
export const BookCard: React.FC<BookCardProps> = ({
  book,
  categoryItem,
  progress,
  onContinueReading,
  onStartFromChapter,
  onDelete,
  onRead,
  coverUrl
}) => {
  const [showChapters, setShowChapters] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<string>('');

  // Check if this is a category item card
  const isCategoryItem = !!categoryItem;

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (isCategoryItem || !progress || !book || book.totalWords === 0) return 0;
    return Math.min(100, Math.round((progress.currentWordIndex / book.totalWords) * 100));
  }, [progress, book, isCategoryItem]);

  // Get chapters with their progress status
  const chaptersWithProgress = useMemo(() => {
    if (!book) return [];
    return book.chapters.map(chapter => {
      const chapterProgress = progress?.chapterProgress.find(
        cp => cp.chapterId === chapter.id
      );
      return {
        ...chapter,
        status: progress ? getChapterStatus(progress, chapter.id) : 'not-started',
        wordIndex: chapterProgress?.wordIndex || 0,
        completed: chapterProgress?.completed || false,
      };
    });
  }, [book, progress]);

  // Get current chapter info
  const currentChapter = useMemo(() => {
    if (!book) return null;
    if (!progress?.currentChapterId) return book.chapters[0] || null;
    return book.chapters.find(c => c.id === progress.currentChapterId) || book.chapters[0];
  }, [book, progress]);

  // Handle continue reading (for processed books)
  const handleContinueReadingBook = () => {
    if (!book) return;
    if (progress && progress.currentChapterId) {
      onContinueReading?.(book.id, progress.currentChapterId, progress.currentWordIndex);
    } else if (book.chapters.length > 0) {
      onContinueReading?.(book.id, book.chapters[0].id, 0);
    }
  };

  // Handle start from chapter
  const handleStartFromChapter = () => {
    if (!book || !selectedChapter) return;
    onStartFromChapter?.(book.id, selectedChapter);
  };

  // Handle read (for category items)
  const handleReadItem = () => {
    if (categoryItem && onRead) {
      onRead(categoryItem);
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (book) {
      onDelete?.(book.id);
      setShowDeleteConfirm(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'text-green-500';
      case 'in-progress':
        return 'text-blue-500';
      default:
        return 'text-gray-400';
    }
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'advanced':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Get cover image or placeholder
  const coverImage = coverUrl || book?.coverUrl;
  const hasCover = !!coverImage;

  // --- Category Item Card Render ---
  if (isCategoryItem && categoryItem) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="flex p-4 gap-4">
          {/* Category Icon */}
          <div className="flex-shrink-0 w-20 h-28 bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center">
            <FolderOpen className="w-8 h-8 text-white/80" />
          </div>

          {/* Item Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {categoryItem.title}
            </h3>
            
            {/* Spoiler-free Summary */}
            {(categoryItem as any).spoilerFreeSummary && (
              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                {(categoryItem as any).spoilerFreeSummary}
              </p>
            )}
            
            {/* Stats */}
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 dark:text-gray-500">
              <span className="flex items-center gap-1">
                <BarChart3 className="h-3 w-3" />
                {categoryItem.wordCount} words
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {categoryItem.readingTimeMinutes} min
              </span>
            </div>

            {/* Difficulty Badge */}
            <div className="mt-2">
              <span className={`px-2 py-0.5 text-xs rounded-full ${getDifficultyColor(categoryItem.difficulty)}`}>
                {categoryItem.difficulty}
              </span>
            </div>

            {/* Source */}
            {categoryItem.source && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Source: {categoryItem.source}
              </p>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="px-4 pb-4">
          <button
            onClick={handleReadItem}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <Play className="h-4 w-4" />
            Start Reading
          </button>
        </div>
      </div>
    );
  }

  // --- Processed Book Card Render ---
  if (!book) return null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      {/* Book Header with Cover */}
      <div className="flex p-4 gap-4">
        {/* Cover Image */}
        <div className="flex-shrink-0 w-20 h-28 bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center overflow-hidden">
          {hasCover ? (
            <img 
              src={coverImage} 
              alt={book.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <BookOpen className="w-8 h-8 text-white/80" />
          )}
        </div>

        {/* Book Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            {book.title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {book.author || 'Unknown Author'}
          </p>
          
          {/* Stats */}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 dark:text-gray-500">
            <span>{formatWordCount(book.totalWords)}</span>
            <span>•</span>
            <span>{book.totalChapters} chapters</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {estimateTime(book.totalWords)}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {progress ? `${progressPercentage}% complete` : 'Not started'}
              </span>
              {currentChapter && (
                <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[150px]">
                  {currentChapter.title}
                </span>
              )}
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Delete Button */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Delete book"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Action Buttons */}
      <div className="px-4 pb-4 flex gap-2">
        <button
          onClick={handleContinueReadingBook}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
        >
          <Play className="h-4 w-4" />
          {progress ? 'Continue' : 'Start Reading'}
        </button>
        
        <button
          onClick={() => setShowChapters(!showChapters)}
          className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <span className="text-sm">Chapters</span>
          {showChapters ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Chapter List */}
      {showChapters && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Chapter to Start
            </h4>
            {selectedChapter && (
              <button
                onClick={handleStartFromChapter}
                className="text-sm px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
              >
                Start Here
              </button>
            )}
          </div>
          
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {chaptersWithProgress.map((chapter) => (
              <div
                key={chapter.id}
                onClick={() => setSelectedChapter(chapter.id)}
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                  selectedChapter === chapter.id
                    ? 'bg-blue-100 dark:bg-blue-900/30'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {/* Status Icon */}
                {chapter.completed ? (
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : chapter.wordIndex > 0 ? (
                  <Circle className="h-4 w-4 text-blue-500 fill-blue-500 flex-shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                )}
                
                {/* Chapter Info */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${
                    selectedChapter === chapter.id
                      ? 'text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {chapter.title}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {formatWordCount(chapter.wordCount)}
                    {chapter.wordIndex > 0 && !chapter.completed && (
                      <> • {Math.round((chapter.wordIndex / chapter.wordCount) * 100)}%</>
                    )}
                  </p>
                </div>

                {/* Mini Progress Bar */}
                {chapter.wordIndex > 0 && !chapter.completed && (
                  <div className="w-16 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(chapter.wordIndex / chapter.wordCount) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete Book?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete "{book.title}"? This will also remove all reading progress. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookCard;
